// useCountdown.ts — tiny hooks for the email-flow polish:
//
//   useExpiryCountdown(expiresAt)  — live mm:ss formatter for the
//                                    verify_token expires_at field
//   useResendCooldown(seconds)     — fire `arm()` to start a cooldown;
//                                    `remaining` ticks down to 0 then
//                                    `ready` becomes true again
//
// Both hooks rely on a 1-second interval. Cleanup is automatic on
// unmount (clearInterval in the effect cleanup).

import React from 'react';

export interface ExpiryCountdown {
  // Seconds remaining until expiresAt; clamped at 0.
  seconds:		number;
  // Formatted "m:ss" string (e.g. "9:34"). Empty when expiresAt is null.
  display:		string;
  // True once seconds <= 0.
  expired:		boolean;
}

export function useExpiryCountdown(expiresAt: Date | null): ExpiryCountdown {
  const [now, setNow] = React.useState(() => new Date());

  React.useEffect(() => {
    if (!expiresAt) return undefined;
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, [expiresAt]);

  if (!expiresAt) {
    return { seconds: 0, display: '', expired: false };
  }
  const ms = expiresAt.getTime() - now.getTime();
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  const display = m + ':' + (s < 10 ? '0' : '') + s;
  return { seconds, display, expired: seconds <= 0 };
}

export interface ResendCooldown {
  // True when the user can fire the "send / resend" button.
  ready:		boolean;
  // Seconds remaining in the cooldown; 0 when ready.
  remaining:	number;
  // Zero-padded "mm:ss" of the remaining time, e.g. "00:27".
  display:		string;
  // Call from the send/resend onClick AFTER the request to start the
  // cooldown clock. Subsequent ticks decrement remaining toward 0.
  arm:			() => void;
}

export function useResendCooldown(seconds: number): ResendCooldown {
  const [armedUntil, setArmedUntil] = React.useState<number | null>(null);
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    if (armedUntil === null) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(tick);
  }, [armedUntil]);

  const remaining = armedUntil === null
    ? 0
    : Math.max(0, Math.ceil((armedUntil - now) / 1000));

  // Once the cooldown elapses, clear the armedUntil so future
  // re-arms work and the next render doesn't keep ticking.
  React.useEffect(() => {
    if (armedUntil !== null && remaining === 0) {
      setArmedUntil(null);
    }
  }, [armedUntil, remaining]);

  const arm = React.useCallback(() => {
    setArmedUntil(Date.now() + seconds * 1000);
  }, [seconds]);

  const m = Math.floor(remaining / 60);
  const s = remaining % 60;
  const display = (m < 10 ? '0' : '') + m + ':' + (s < 10 ? '0' : '') + s;

  return {
    ready: armedUntil === null,
    remaining,
    display,
    arm,
  };
}
